import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, ColorPicker, Switch, Select, Spin } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import axiosInstance from '../apis/axiosInstance';
import { BASE_URL } from '../apis/constant';
import { useAdminControl } from "../store/Store";

import EditRelatedProducts from './EditRelatedProducts';

const ProductEditForm = ({ showMessage }) => {
  const createEmptyVariant = () => ({
    id: `temp_${Math.random().toString(36).substr(2, 9)}`,
    name: '',
    colorValue: '#1677ff',
    priceInc: '',
    inStock: true,
    visible: true
  });

  const [originalData, setOriginalData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [featureInput, setFeatureInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggedGalleryIndex, setDraggedGalleryIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);

  const [allCategories, setAllCategories] = useState({ data: {}, loading: false });
  const [tagTypesState, setTagTypesState] = useState({ data: {}, loading: false });
  const [productData, setProductData] = useState({ data: null, loading: false });
  const [publishStatuses, setPublishStatuses] = useState({ data: {}, loading: false });

  const [relatedProducts, setRelatedProducts] = useState([]);
const [isRelatedProductsUpdated, setIsRelatedProductsUpdated] = useState(false);




  const { selectedProduct, reRender } = useAdminControl();

  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      setAllCategories({ data: {}, loading: true });

      try {
        const response = await axiosInstance.post('/api/get_all_categories_simple/');
        setAllCategories({
          data: response.data.data,
          loading: false
        });

      } catch (error) {
        console.error('Error fetching categories:', error);
        setAllCategories({
          data: {},
          loading: false
        });
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchTagTypes = async () => {
      setTagTypesState({ data: {}, loading: true });

      try {
        const response = await axiosInstance.post('/api/get_all_tag_types/');
        setTagTypesState({
          data: response.data.data,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching tag types:', error);
        setTagTypesState({
          data: {},
          loading: false
        });
      }
    };

    fetchTagTypes();
  }, []);

  useEffect(() => {
    const fetchPublishStatuses = async () => {
      setPublishStatuses({ data: {}, loading: true });

      try {
        const response = await axiosInstance.post('/api/get_all_publish_statuses/');
        setPublishStatuses({
          data: response.data.data,
          loading: false
        });

      } catch (error) {
        console.error('Error fetching publish statuses:', error);
        setPublishStatuses({
          data: {},
          loading: false
        });
      }
    };

    fetchPublishStatuses();
  }, []);

  useEffect(() => {
    // Don't fetch if no product is selected (drawer is closed)
    if (!selectedProduct) {
      setProductData({ data: null, loading: false, error: null });
      setFormData(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchProduct = async () => {
      if (isMounted) {
        setProductData({ data: null, loading: true, error: null });
      }

      try {
        const response = await axiosInstance.post('/api/get_product_detail_by_id/',
          { product_id: selectedProduct }
        );

        if (isMounted) {
          setProductData({
            data: response.data,
            loading: false,
            error: null
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching product detail:", err);
          setProductData({
            data: null,
            loading: false,
            error: err.response?.data || err.message
          });
        }
      }
    };

    fetchProduct();

    return () => {
      isMounted = false;
    };
  }, [selectedProduct]);

  useEffect(() => {
    if (formData?.tagTypeId && tagTypesState.data[formData.tagTypeId]) {
      const tagData = tagTypesState.data[formData.tagTypeId];
      if (tagData.text && tagData.text.trim() !== '') {
        setFormData(prev => ({
          ...prev,
          tagValue: tagData.text
        }));

        // Clear validation error if text is auto-filled
        if (validationErrors.tagValue) {
          setValidationErrors(prev => ({ ...prev, tagValue: false }));
        }
      } else {
        // If text is empty, clear the tagValue field
        setFormData(prev => ({
          ...prev,
          tagValue: ''
        }));
      }
    }
  }, [formData?.tagTypeId, tagTypesState.data, validationErrors]);

  // Helper: Transform database data to form data
  const transformToFormData = (dbData) => {
    return {
      productName: dbData.productName || '',
      categoryId: dbData.categoryId?.toString() || null,
      description: dbData.description || '',
      tagTypeId: dbData.tagTypeId?.toString() || null,
      tagValue: dbData.tagValue || '',
      basePrice: dbData.basePrice || '',
      offerPrice: dbData.offerPrice || '',
      rating: dbData.rating || '',
      ratingCount: dbData.ratingCount || '',
      features: [...(dbData.features || [])],
      image: null,
      // Add BASE_URL here for main image
      imagePreview: dbData.mainImage?.url
        ? `${BASE_URL}${dbData.mainImage.url}`
        : null,
      existingImageUrl: dbData.mainImage?.url,
      hasVariants: dbData.hasVariants || false,
      variantType: dbData.variantType || 'Color',
      variants: [
        ...(dbData.variants || []).map(v => ({
          ...v,
          id: v.id?.toString() || `var_${Math.random().toString(36).substr(2, 9)}`, // Ensure id is string
          priceInc: v.priceInc?.toString() || ''
        })),
        createEmptyVariant()
      ],
      // Add BASE_URL here for gallery images
      galleryImages: (dbData.galleryImages || []).map((img, index) => ({
        ...img,
        id: img.id?.toString() || `gallery_${Math.random().toString(36).substr(2, 9)}`, // Ensure id is string
        file: null,
        // Add BASE_URL prefix if it's not already a full URL
        previewUrl: img.url
          ? (img.url.startsWith('http') ? img.url : `${BASE_URL}${img.url}`)
          : null,
        order: img.order || index,
        assignedVariantId: img.assignedVariantId?.toString() || null // Ensure this is string too
      })),
      productId: dbData.id?.toString() || '',
      publishStatusId: dbData.publishStatus?.toString() || Object.keys(publishStatuses.data)[0]?.toString() || '',

      relatedProducts: dbData.relatedProducts || [],
    };
  };

  // Initialize form data
  useEffect(() => {
    if (!productData.data) return;  // Wait until data is loaded

    const formDataFromDB = transformToFormData(productData.data);
    setOriginalData(JSON.parse(JSON.stringify(formDataFromDB)));
    setFormData(formDataFromDB);
    setIsLoading(false);

  }, [productData.data]);  

  // Check if form has changed
useEffect(() => {
    if (!originalData || !formData) return;

    const formChanged = JSON.stringify(originalData) !== JSON.stringify(formData);
    setIsFormChanged(formChanged || isRelatedProductsUpdated);
}, [formData, originalData, isRelatedProductsUpdated]);

  // Helper: Crop image to 1:1 Square
  const cropToSquare = (file, maxDimension = 2000) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const minSize = Math.min(img.width, img.height);
        const size = Math.min(minSize, maxDimension);

        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          const sx = (img.width - minSize) / 2;
          const sy = (img.height - minSize) / 2;

          ctx.drawImage(img, sx, sy, minSize, minSize, 0, 0, size, size);
          canvas.toBlob((blob) => {
            const croppedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(croppedFile);
          }, file.type, 0.85);
        } else {
          resolve(file); // fallback
        }
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};

    if (!formData.productName.trim()) errors.productName = true;
    if (!formData.description.trim()) errors.description = true;
    if (!formData.categoryId) errors.categoryId = true;
    if (formData.tagTypeId && !formData.tagValue.trim()) errors.tagValue = true;
    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) errors.basePrice = true;
    if (!formData.imagePreview) errors.image = true;
    if (formData.features.length === 0) errors.features = true;

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // FIXED: Handlers with proper number handling - NO floating point errors
  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Handle number inputs specifically
    const numberFields = ['basePrice', 'offerPrice', 'rating', 'ratingCount'];
    if (numberFields.includes(name)) {
      // Allow empty string for deletion
      if (value === '') {
        processedValue = '';
      } else if (name === 'ratingCount') {
        // For ratingCount, use parseInt to avoid floating point issues
        const num = parseInt(value, 10);
        processedValue = isNaN(num) ? '' : num.toString();
      } else {
        // For other number fields, handle decimal properly
        // Use regex to only allow numbers and one decimal point
        const sanitized = value.replace(/[^\d.]/g, '');

        // Ensure only one decimal point
        const parts = sanitized.split('.');
        if (parts.length > 2) {
          processedValue = parts[0] + '.' + parts.slice(1).join('');
        } else {
          processedValue = sanitized;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  // FIXED: Handler for number field blur events - PRECISE handling
  const handleNumberBlur = (e) => {
    const { name, value } = e.target;

    if (value === '' || value === undefined) return;

    let processedValue = value;

    switch (name) {
      case 'basePrice':
      case 'offerPrice':
        // Use precise decimal handling
        const priceMatch = value.match(/^\d+(\.\d{0,2})?/);
        if (priceMatch) {
          const num = parseFloat(priceMatch[0]);
          if (!isNaN(num)) {
            // Round to 2 decimal places using toFixed
            processedValue = Math.max(0, num).toFixed(2);
          }
        }
        break;

      case 'rating':
        // Use precise decimal handling for rating
        const ratingMatch = value.match(/^\d+(\.\d{0,1})?/);
        if (ratingMatch) {
          let num = parseFloat(ratingMatch[0]);
          if (!isNaN(num)) {
            // Clamp between 0-5 and round to 1 decimal
            num = Math.min(5, Math.max(0, num));
            processedValue = num.toFixed(1);

            // Ensure it's exactly one decimal place
            if (processedValue.endsWith('.0')) {
              processedValue = processedValue.slice(0, -2);
            }
          }
        }
        break;

      case 'ratingCount':
        // Use parseInt for whole numbers
        const countNum = parseInt(value, 10);
        if (!isNaN(countNum)) {
          processedValue = Math.max(0, countNum).toString();
        } else {
          processedValue = '0';
        }
        break;

      default:
        break;
    }

    // Only update if value changed
    if (processedValue !== value && processedValue !== undefined) {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const croppedFile = await cropToSquare(file, 2000);
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            image: croppedFile,
            imagePreview: reader.result
          }));
        };
        reader.readAsDataURL(croppedFile);
      } catch (error) {
        console.error('Error cropping image:', error);
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            image: file,
            imagePreview: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageUpload({ target: { files: [file] } });
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleFeatureKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFeature();
    }
  };

  // Variant Handlers
  const toggleHasVariants = () => {
    setFormData(prev => ({
      ...prev,
      hasVariants: !prev.hasVariants
    }));
  };

  const setVariantType = (type) => {
    setFormData(prev => ({
      ...prev,
      variantType: type,
      variants: [createEmptyVariant()]
    }));
  };

  // FIXED: Variant change handler with proper number handling
  const handleVariantChange = (id, field, value) => {
    setFormData(prev => {
      let processedValue = value;

      // Handle priceInc specifically
      if (field === 'priceInc') {
        if (value === '') {
          processedValue = '';
        } else {
          // Use regex to sanitize input
          const sanitized = value.replace(/[^\d.]/g, '');

          // Ensure only one decimal point
          const parts = sanitized.split('.');
          if (parts.length > 2) {
            processedValue = parts[0] + '.' + parts.slice(1).join('');
          } else {
            processedValue = sanitized;
          }
        }
      }

      let newVariants = prev.variants.map(v =>
        v.id === id ? {
          ...v,
          [field]: field === 'priceInc' ? processedValue : value
        } : v
      );

      if (field === 'name') {
        const index = newVariants.findIndex(v => v.id === id);
        const isLastRow = index === newVariants.length - 1;
        const nameValue = String(value).trim();

        if (isLastRow && nameValue !== '') {
          newVariants.push(createEmptyVariant());
        }

        if (!isLastRow && nameValue === '') {
          const nextVariant = newVariants[index + 1];
          if (nextVariant && nextVariant.name.trim() === '') {
            newVariants.splice(index + 1, 1);
          }
        }
      }

      return { ...prev, variants: newVariants };
    });
  };

  // FIXED: Handler for variant price blur events
  const handleVariantPriceBlur = (id, value) => {
    if (value === '' || value === undefined) return;

    // Use regex to get valid number
    const priceMatch = value.match(/^\d+(\.\d{0,2})?/);
    if (priceMatch) {
      const num = parseFloat(priceMatch[0]);
      if (!isNaN(num)) {
        const formattedValue = Math.max(0, num).toFixed(2);
        if (formattedValue !== value) {
          handleVariantChange(id, 'priceInc', formattedValue);
        }
      }
    }
  };

  const handleDeleteVariant = (index) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      newVariants.splice(index, 1);

      if (newVariants.length === 0) {
        newVariants.push(createEmptyVariant());
      } else {
        const lastVariant = newVariants[newVariants.length - 1];
        if (lastVariant.name.trim() !== '') {
          newVariants.push(createEmptyVariant());
        }
      }

      return { ...prev, variants: newVariants };
    });
  };

  // Gallery Handlers
  const handleGalleryUpload = async (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newImages = [];

      await Promise.all(
        files.map(async (file) => {
          if (!file.type.startsWith('image/')) return;

          const croppedFile = await cropToSquare(file, 2000);
          const thumbFile = await cropToSquare(file, 600);
          const thumbPreviewUrl = URL.createObjectURL(thumbFile);

          newImages.push({
            id: `temp_gallery_${Math.random().toString(36).substr(2, 9)}`,
            file: croppedFile,
            previewUrl: thumbPreviewUrl,
            assignedVariantId: null,
            order: formData.galleryImages.length + newImages.length
          });
        })
      );

      setFormData(prev => ({
        ...prev,
        galleryImages: [...prev.galleryImages, ...newImages],
      }));
    }
  };

  const handleRemoveGalleryImage = (id) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(img => img.id !== id)
    }));
  };

  const handleAssignGalleryVariant = (imageId, variantId) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.map(img =>
        img.id === imageId ? { ...img, assignedVariantId: variantId } : img
      )
    }));
  };

  const handleGalleryDragStart = (index) => {
    setDraggedGalleryIndex(index);
  };

  const handleGalleryDragOver = (e, index) => {
    e.preventDefault();
    if (draggedGalleryIndex === null || draggedGalleryIndex === index) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const position = x > rect.width / 2 ? 'right' : 'left';

    setDropTarget({ index, position });
  };

  const handleGalleryDrop = (e) => {
    e.preventDefault();

    if (draggedGalleryIndex !== null && dropTarget) {
      const newGallery = [...formData.galleryImages];
      const draggedItem = newGallery[draggedGalleryIndex];

      newGallery.splice(draggedGalleryIndex, 1);

      let insertionIndex = dropTarget.index;
      if (dropTarget.position === 'right') {
        insertionIndex += 1;
      }

      if (draggedGalleryIndex < insertionIndex) {
        insertionIndex -= 1;
      }

      newGallery.splice(insertionIndex, 0, draggedItem);

      // Update order
      const updatedGallery = newGallery.map((img, idx) => ({
        ...img,
        order: idx
      }));

      setFormData(prev => ({ ...prev, galleryImages: updatedGallery }));
    }

    setDraggedGalleryIndex(null);
    setDropTarget(null);
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData(JSON.parse(JSON.stringify(originalData)));
      setValidationErrors({});
      setIsFormChanged(false);
    }
  };

  const handleUpdate = async () => {
    const isValid = validateForm();
    if (!isValid) {
      const firstError = Object.keys(validationErrors)[0];
      if (firstError) {
        const element = document.querySelector(`[name="${firstError}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    try {
      setIsLoading(true);

      // Prepare FormData
      const formDataToSend = new FormData();

      formDataToSend.append('productId', formData.productId);
      formDataToSend.append('productName', formData.productName);
      formDataToSend.append('categoryId', formData.categoryId || '');
      formDataToSend.append('description', formData.description);
      formDataToSend.append('tagTypeId', formData.tagTypeId || '');
      formDataToSend.append('tagValue', formData.tagValue);
      formDataToSend.append('basePrice', formData.basePrice);
      formDataToSend.append('offerPrice', formData.offerPrice || '');
      formDataToSend.append('rating', formData.rating || '');
      formDataToSend.append('ratingCount', formData.ratingCount || '0');
      formDataToSend.append('hasVariants', formData.hasVariants.toString());
      formDataToSend.append('variantType', formData.variantType);
      formDataToSend.append('mainImageChanged', !!formData.image ? 'true' : 'false');
      formDataToSend.append('publishStatusId', formData.publishStatusId || '');
      formDataToSend.append('relatedProducts', JSON.stringify(relatedProducts));

      // 2. JSON Arrays
      formDataToSend.append('features', JSON.stringify(formData.features));

      // Prepare variants data
      const cleanVariants = formData.variants
        .filter(variant => variant.name.trim() !== '')
        .map((v) => ({
          id: v.id.startsWith('temp_') ? null : v.id,
          frontendId: v.id,
          name: v.name,
          colorValue: formData.variantType === 'Color' ? v.colorValue : null,
          priceInc: v.priceInc || '0.00',
          inStock: v.inStock,
          visible: v.visible
        }));

      formDataToSend.append('variants', JSON.stringify(cleanVariants));

      // 3. Gallery Images Data
      const galleryUpdates = formData.galleryImages.map((img, index) => ({
        id: img.id,
        assignedVariantId: img.assignedVariantId,
        order: img.order,
        isNew: img.id.startsWith('temp_gallery_'),
        index: img.id.startsWith('temp_gallery_') ? index : undefined
      }));

      formDataToSend.append('galleryImages', JSON.stringify(galleryUpdates));

      // Main Image (only if changed)
      if (formData.image) {
        formDataToSend.append('mainImage', formData.image);
      }

      // New Gallery Images (only new ones)
      const newGalleryImages = formData.galleryImages.filter(img =>
        img.id.startsWith('temp_gallery_') && img.file
      );

      newGalleryImages.forEach(img => {
        if (img.file) {
          formDataToSend.append('galleryImages', img.file);
        }
      });

      // Call API
      const updateResponse = await axiosInstance.post('/api/update_product/', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle success
      if (updateResponse.data.success) {
        setOriginalData(JSON.parse(JSON.stringify(formData)));
        setIsFormChanged(false);
        reRender();
        showMessage('success', 'Product Updated Successfully!');

        if (selectedProduct) {
          const productDetailResponse = await axiosInstance.post('/api/get_product_detail_by_id/',
            { product_id: selectedProduct }
          );
          const refreshedData = transformToFormData(productDetailResponse.data);
          setOriginalData(JSON.parse(JSON.stringify(refreshedData)));
          setFormData(refreshedData);
        }
      } else {
        showMessage('error', updateResponse.data.error || 'Product Update Failed!');
      }

    } catch (err) {
      showMessage('error',
        err.response?.data?.error ||
        err.response?.data?.details ||
        err.message ||
        'Product Update Failed!'
      );
    } finally {
      setIsLoading(false);
    };
  };

  // Helper function to get current tag type display value
  const getCurrentTagTypeDisplay = () => {
    if (!formData?.tagTypeId || !tagTypesState.data[formData.tagTypeId]) {
      return '';
    }
    return tagTypesState.data[formData.tagTypeId].tag || '';
  };

  if (isLoading || !formData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }





  //! MOCK DATA
  const publishStatus = {
    3546: { status: "published", color: "green" },
    3547: { status: "draft", color: "yellow" },
    3548: { status: "paused", color: "gray" }
  };

  return (
    <div className="w-full overflow-hidden flex flex-col">
      {/* TOP SECTION: Form Inputs & Image */}
      <div className="flex flex-col md:flex-row">
        {/* LEFT PANEL: Form Fields */}
        <div className="flex-1 p-5 md:p-6 flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Product
            </h2>
            <div className="flex items-center gap-2">
              {isFormChanged && (
                <span className="text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full border border-yellow-100 animate-pulse">
                  Unsaved Changes
                </span>
              )}

              {/* Status Selector - Compact */}
              <div className="w-28">
                <Select
  name="publishStatusId"
  value={formData.publishStatusId || ''}
  onChange={(v) => handleChange({ target: { name: "publishStatusId", value: v } })}
  placeholder="Status"
  options={Object.entries(publishStatuses.data).map(([id, data]) => ({
    value: id,
    label: (
      <div className="flex items-center gap-2 py-1">
        <div
          className="w-2 h-2 rounded-full"
          style={{ 
            backgroundColor: data.color === 'green' ? '#10B981' : 
                           data.color === 'orange' ? '#F59E0B' : 
                           data.color === 'default' ? '#6B7280' : 
                           data.color || '#6B7280' 
          }}
        />
        <span className="text-xs font-medium capitalize">{data.status}</span>
      </div>
    )
  }))}
  style={{
    width: '100%',
    fontSize: '12px',
    height: '28px',
    padding: '0 8px',
    background: 'transparent'
  }}
  dropdownStyle={{
    fontSize: '12px',
    minWidth: '100px'
  }}
  variant="borderless"
  size="small"
/>
              </div>

              <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full border border-gray-100">
                ID: {formData.productId}
              </span>
            </div>
          </div>

          {/* Row 1: Name & Category */}
          <div className="grid grid-cols-12 gap-3">
            {/* Product Name */}
            <div className="col-span-8 group relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className={`h-4 w-4 ${validationErrors.productName ? 'text-red-500' : 'text-gray-400 group-focus-within:text-brand-500'} transition-colors`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </div>
              <input
                type="text"
                name="productName"
                placeholder="Product Name"
                value={formData.productName}
                onChange={handleChange}
                className={`w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none transition-all placeholder:text-gray-400 ${validationErrors.productName
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
            </div>

            {/* Category Select */}
            <div className="col-span-4 relative group">
              <Select
                className="transparent-select"
                name="categoryId"
                value={formData.categoryId}
                onChange={(v) => {
                  handleChange({ target: { name: "categoryId", value: v } });
                  if (validationErrors.categoryId) {
                    setValidationErrors(prev => ({ ...prev, categoryId: false }));
                  }
                }}
                showSearch
                placeholder="Select category"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{
                  width: "100%",
                  paddingLeft: "10px",
                  background: "#f9fafb",
                  border: validationErrors.categoryId ? "1px solid #ef4444" : "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  minHeight: '40px'
                }}
                options={Object.entries(allCategories.data).map(([id, label]) => ({ value: id, label }))}
              />
            </div>
          </div>

          {/* Row 2: Description */}
          <div className="relative group">
            <textarea
              name="description"
              rows={2}
              placeholder="Product description..."
              value={formData.description}
              onChange={handleChange}
              className={`w-full p-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-gray-400 resize-none ${validationErrors.description
                ? 'border-red-500 focus:ring-red-500/20'
                : 'border-gray-200 focus:ring-brand-500/20'
                }`}
            />
          </div>

          {/* Row 3: Prices & Ratings */}
          <div className="grid grid-cols-4 gap-3">
            {/* Base Price - FIXED */}
            <div className="col-span-1 relative group">
              <input
                type="number"
                step="0.01"
                min="0"
                name="basePrice"
                placeholder="Base Price"
                value={formData.basePrice}
                onChange={handleChange}
                onBlur={handleNumberBlur}
                className={`w-full pl-5 pr-2 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none ${validationErrors.basePrice
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
            </div>

            {/* Offer Price - FIXED */}
            <div className="col-span-1 relative group">
              <input
                type="number"
                step="0.01"
                min="0"
                name="offerPrice"
                placeholder="Offer Price"
                value={formData.offerPrice}
                onChange={handleChange}
                onBlur={handleNumberBlur}
                className="w-full pl-5 pr-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none text-green-700 font-medium"
              />
            </div>

            {/* Rating - FIXED */}
            <div className="col-span-1 relative group">
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <svg className="h-3 w-3 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                name="rating"
                placeholder="0-5"
                value={formData.rating}
                onChange={handleChange}
                onBlur={handleNumberBlur}
                className="w-full pl-7 pr-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              />
            </div>

            {/* Rating Count - FIXED */}
            <div className="col-span-1 relative">
              <input
                type="text"
                step="1"
                min="0"
                name="ratingCount"
                placeholder="Count"
                value={formData.ratingCount}
                onChange={handleChange}
                onBlur={handleNumberBlur}
                className="w-full px-2 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
              />
            </div>
          </div>

          {/* Row 4: Tag System */}
          <div className={`grid grid-cols-12 gap-0 items-center bg-gray-50 rounded-lg border ${validationErrors.tagValue ? 'border-red-500' : 'border-gray-200'}`}>
            <div className="col-span-4 relative">
              <Select
                className="tag-type-select"
                name="tagTypeId"
                value={formData.tagTypeId}
                onChange={(v) => {
                  handleChange({ target: { name: "tagTypeId", value: v } });
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                onClear={() => {
                  handleChange({ target: { name: "tagTypeId", value: null } });
                  setFormData(prev => ({ ...prev, tagValue: '' }));
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                allowClear
                showSearch
                placeholder="Select tag type"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                variant="borderless"
                style={{
                  width: '100%',
                  fontSize: "12px",
                  fontWeight: "450",
                  padding: '0 12px',
                  margin: '0',
                  backgroundColor: 'transparent'
                }}
                dropdownStyle={{
                  fontSize: "12px",
                }}
                popupMatchSelectWidth={false}
                options={Object.entries(tagTypesState.data).map(([id, tagData]) => ({
                  value: id,
                  label: tagData.tag
                }))}
              />
              {validationErrors.tagValue && (
                <div className="absolute right-0 top-0 bottom-0 w-px bg-red-500"></div>
              )}
            </div>

            <div className="col-span-8 relative">
              <svg className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 ${validationErrors.tagValue ? 'text-red-500' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <input
                type="text"
                name="tagValue"
                placeholder={getCurrentTagTypeDisplay()
                  ? `Enter ${getCurrentTagTypeDisplay().toLowerCase()} text...`
                  : 'Select tag type first...'
                }
                value={formData.tagValue}
                onChange={(e) => {
                  handleChange(e);
                  if (validationErrors.tagValue) {
                    setValidationErrors(prev => ({ ...prev, tagValue: false }));
                  }
                }}
                disabled={!formData.tagTypeId}
                className={`w-full pl-9 pr-3 py-2.5 text-sm bg-transparent outline-none placeholder:text-gray-400 ${validationErrors.tagValue ? 'text-red-500' : ''} ${!formData.tagTypeId ? 'cursor-not-allowed opacity-50' : ''
                  }`}
              />
            </div>
          </div>
          {/* Row 5: Features */}
          <div className="flex flex-col gap-2">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Add feature..."
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyDown={handleFeatureKeyDown}
                className={`w-full pl-3 pr-10 py-2 text-sm bg-gray-50 border rounded-lg focus:ring-2 focus:border-brand-500 outline-none transition-all ${validationErrors.features
                  ? 'border-red-500 focus:ring-red-500/20'
                  : 'border-gray-200 focus:ring-brand-500/20'
                  }`}
              />
              <button
                onClick={addFeature}
                className="absolute right-1.5 p-1 bg-brand-500 hover:bg-brand-600 text-white rounded-md transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.features.map((feature, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border group animate-in fade-in zoom-in duration-200 ${validationErrors.features
                    ? 'bg-red-50 text-red-700 border-red-100'
                    : 'bg-brand-50 text-brand-700 border-brand-100'
                    }`}
                >
                  {feature}
                  <button
                    onClick={() => removeFeature(idx)}
                    className="hover:text-red-500 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Image Upload (Width Fixed) */}
        <div className="w-full md:w-[240px] bg-gray-50 md:border-l border-gray-100 p-6 flex flex-col items-center border-t md:border-t-0">
          <label className="text-sm font-semibold text-gray-600 w-full text-left mb-4">Main Image</label>

          <div
            className={`
              relative w-full aspect-square max-w-[200px] rounded-xl border-2 border-dashed 
              flex flex-col items-center justify-center cursor-pointer transition-all duration-300
              ${validationErrors.image
                ? 'border-red-500 bg-red-50'
                : isDragOver
                  ? 'border-brand-500 bg-brand-50 scale-105'
                  : 'border-gray-300 bg-white hover:border-brand-400 hover:bg-gray-50'
              }
              ${formData.imagePreview ? 'border-none p-0 overflow-hidden' : ''}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {formData.imagePreview ? (
              <div className="relative w-full h-full group">
                <img
                  src={formData.imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                  <p className="text-white text-xs font-medium">Change Image</p>
                </div>
                {formData.image && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                    New
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center p-4">
                <div className={`p-3 rounded-full mb-3 ${validationErrors.image ? 'bg-red-50 text-red-500' : 'bg-brand-50 text-brand-500'
                  } group-hover:scale-110 transition-transform`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500 font-medium">
                  Drag & Drop or <span className="text-brand-600 underline">Browse</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-1">1:1 ratio, auto-cropped</p>
              </div>
            )}

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
              accept="image/*"
            />
          </div>

          <div className="flex items-center justify-between mb-4 w-full pt-[20px] select-none">
            <span className="text-sm font-semibold text-gray-700">Product Variants</span>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.hasVariants}
                onChange={toggleHasVariants}
                size="default"
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </div>
          </div>

          {formData.hasVariants && (
            <div className="flex p-1 bg-gray-100 rounded-lg mb-3 w-full select-none">
              <button
                onClick={() => setVariantType('Color')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-xs font-medium transition-all flex-1 ${formData.variantType === 'Color'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                Color
              </button>

              <div className="w-px bg-gray-300 mx-1"></div>

              <button
                onClick={() => setVariantType('Size')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-xs font-medium transition-all flex-1 ${formData.variantType === 'Size'
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
                Size
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MIDDLE SECTION: Variants (Full Width) */}
      {formData.hasVariants && (
        <div className="border-t border-gray-100 p-5 md:p-6 bg-gray-50/30 select-none">
          <div className="animate-in slide-in-from-top-2 duration-300">
            <div className="w-full overflow-x-auto rounded-lg border border-gray-100 bg-white">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/60 backdrop-blur-sm text-center">
                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-1/3">
                      Variant Name
                    </th>
                    {formData.variantType === "Color" && (
                      <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-[140px]">
                        Color
                      </th>
                    )}
                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-20">
                      Stock
                    </th>
                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-24">
                      Price (+)
                    </th>
                    <th className="py-3 text-[11px] font-medium uppercase tracking-wide text-gray-600 w-16">
                      Visible
                    </th>
                    <th className="py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {formData.variants.map((variant, index) => (
                    <tr
                      key={variant.id}
                      className="group border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-2.5 px-3">
                        <input
                          type="text"
                          placeholder={index === formData.variants.length - 1 ? "Type to add variant..." : ""}
                          value={variant.name}
                          onChange={(e) => handleVariantChange(variant.id, "name", e.target.value)}
                          className="w-full bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-[14px] placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 focus:shadow-sm transition-all"
                        />
                      </td>
                      {formData.variantType === "Color" && (
                        <td className="py-2.5 text-center">
                          <ColorPicker
                            value={variant.colorValue}
                            onChange={(color) => {
                              const hex = typeof color === "string" ? color : color.toHexString?.() || "#000000";
                              handleVariantChange(variant.id, "colorValue", hex);
                            }}
                            size="medium"
                            showText
                          />
                        </td>
                      )}
                      <td className="py-2.5 text-center">
                        <Switch
                          checked={variant.inStock}
                          onChange={(checked) => handleVariantChange(variant.id, "inStock", checked)}
                          size="medium"
                        />
                      </td>
                      <td className="py-2.5 px-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                            ৳
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={variant.priceInc}
                            onChange={(e) => handleVariantChange(variant.id, "priceInc", e.target.value)}
                            onBlur={(e) => handleVariantPriceBlur(variant.id, e.target.value)}
                            placeholder="0.00"
                            className="w-full bg-white border border-gray-300 rounded-md pl-6 pr-2 py-1.5 text-[14px] text-right focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 focus:shadow-sm transition-all"
                          />
                        </div>
                      </td>
                      <td className="py-2.5 text-center">
                        <button
                          onClick={() => handleVariantChange(variant.id, "visible", !variant.visible)}
                          className={`p-1.5 rounded-md transition-colors ${variant.visible
                            ? "bg-blue-50 text-blue-600 hover:bg-blue-100"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                            }`}
                        >
                          {variant.visible ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M9.88 9.88a3 3 0 014.24 4.24M3.59 6.59A9.98 9.98 0 0112 5c4.48 0 8.27 2.94 9.54 7a10.02 10.02 0 01-4.13 5.41" />
                            </svg>
                          )}
                        </button>
                      </td>
                      <td className="py-2.5 text-center pr-3">
                        {index !== formData.variants.length - 1 && !variant.id.startsWith('temp_') && (
                          <button
                            onClick={() => handleDeleteVariant(index)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10px] text-gray-400 pl-1">
              * Fill the last row to automatically add a new variant.
            </p>
          </div>
        </div>
      )}

      {/* GALLERY SECTION: Full Width */}
      <div className="border-t border-gray-100 p-5 md:p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Product Gallery
          </h3>
          <button
            onClick={() => galleryInputRef.current?.click()}
            className="px-4 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium flex items-center gap-2 transition-all shadow-sm active:scale-95"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Images
          </button>
          <input
            type="file"
            multiple
            ref={galleryInputRef}
            onChange={handleGalleryUpload}
            className="hidden"
            accept="image/*"
          />
        </div>

        {formData.galleryImages.length === 0 ? (
          <div
            onClick={() => galleryInputRef.current?.click()}
            className="w-full h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center text-gray-400 hover:border-brand-400 hover:bg-brand-50/50 hover:text-brand-500 transition-all cursor-pointer gap-2"
          >
            <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-medium">Click to upload multiple product images</span>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleGalleryDrop}
          >
            {formData.galleryImages.map((img, index) => (
              <div
                key={img.id}
                draggable
                onDragStart={() => handleGalleryDragStart(index)}
                onDragOver={(e) => handleGalleryDragOver(e, index)}
                onDragLeave={() => { }}
                className={`group relative bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col ${draggedGalleryIndex === index ? 'opacity-40 border-dashed border-gray-400' : 'border-gray-100'
                  }`}
                style={{ height: '180px' }}
              >
                {dropTarget?.index === index && draggedGalleryIndex !== index && (
                  <div
                    className={`absolute top-0 bottom-0 w-1 bg-brand-500 z-50 shadow-[0_0_8px_rgba(14,165,233,0.8)] ${dropTarget.position === 'left' ? 'left-0' : 'right-0'
                      }`}
                  />
                )}

                <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleRemoveGalleryImage(img.id)}
                    className="p-1.5 bg-white/90 text-red-500 rounded-full hover:bg-red-50 shadow-sm backdrop-blur-sm"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                <div className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="p-1.5 bg-white/90 text-gray-500 rounded-md shadow-sm backdrop-blur-sm">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                  </div>
                </div>

                {img.id.startsWith('temp_gallery_') && (
                  <div className="absolute top-2 left-2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    New
                  </div>
                )}

                <div className="flex-1 flex items-center justify-center p-3 bg-gray-50/50">
                  <img
                    src={img.previewUrl}
                    alt={`Gallery ${index}`}
                    className="w-[100px] h-[100px] object-cover rounded-lg shadow-sm"
                  />
                </div>

                <div className="p-2 border-t border-gray-100 bg-white">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-gray-100 text-[10px] font-bold text-gray-500 rounded-md">
                      {img.order + 1}
                    </span>
                    {formData.hasVariants && formData.variantType === 'Color' ? (
                      <select
                        value={img.assignedVariantId || ''}
                        onChange={(e) => handleAssignGalleryVariant(img.id, e.target.value)}
                        className="flex-1 text-[10px] py-1 pl-1 pr-4 bg-gray-50 border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500 outline-none text-gray-600 appearance-none cursor-pointer truncate"
                        style={{
                          backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 4px center',
                          backgroundSize: '8px'
                        }}
                      >
                        <option value="">All</option>
                        {formData.variants
                          .filter(v => v.name.trim() !== '')
                          .map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))
                        }
                      </select>
                    ) : (
                      <span className="text-[10px] text-gray-400 font-medium px-1">Global</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RELATED PRODUCTS SECTION */}
<EditRelatedProducts
    currentProductId={formData?.productId}
    initialRelatedProducts={formData?.relatedProducts || []}
    onRelatedProductsChange={(products, hasChanged) => {
        setRelatedProducts(products);
        setIsRelatedProductsUpdated(hasChanged); // This will be true/false
    }}
/>

      {/* FOOTER: Actions */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
        <Button
          color="default"
          variant="dashed"
          size='large'
          style={{ minWidth: '120px' }}
          onClick={handleReset}
          disabled={!isFormChanged}
        >
          Reset
        </Button>
        <Button
          color="primary"
          variant="solid"
          size='large'
          style={{ minWidth: '120px' }}
          onClick={handleUpdate}
          disabled={!isFormChanged || isLoading}
          loading={isLoading}
        >
          Update
        </Button>
      </div>
    </div>
  );
};

export default ProductEditForm;